import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Queue } from '@rossum/api-client/queues';
import {
  AnnotationListColumn,
  UserTableConfig,
} from '@rossum/api-client/shared';
import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GridEventListener,
  GridPinnedColumnFields,
} from '@rossum/ui/x-data-grid-pro';
import { safeOrganizationSelector } from '../../../redux/modules/organization/selectors';
import { updateUiSettings } from '../../../redux/modules/user/actions';
import {
  isUserAdmin,
  userSelector,
} from '../../../redux/modules/user/selectors';
import { swapItemsInArray } from '../../../redux/modules/utils';
import { getColumnName } from '../../document-list-columns/utils';
import { getAnnotationListColumns, preserveMissingColumns } from '../helpers';
import { PanelColumn } from '../types';
import { useFetchOrganization } from './useFetchOrganization';
import { usePatchTableConfig } from './usePatchTableConfig';
import { useTableConfig } from './useTableConfig';

type UseCustomizeTable = {
  activeQueue: Queue | null;
  columns: Array<PanelColumn>;
};

const initialPinnedColumns: Required<GridPinnedColumnFields> = {
  right: ['actions'],
  left: [GRID_CHECKBOX_SELECTION_FIELD],
};

export const useCustomizeTable = ({
  activeQueue,
  columns,
}: UseCustomizeTable) => {
  const pinnedColumns =
    useRef<GridPinnedColumnFields>(initialPinnedColumns).current;

  const orgId = useSelector(safeOrganizationSelector)?.id;

  const { data: organization } = useFetchOrganization({ orgId });

  const { columns: tableConfigColumns } = useTableConfig({
    queueId: activeQueue?.id ?? 0,
    orgId: organization?.id ?? 0,
  });

  const isUserAdminLike = useSelector(isUserAdmin);

  const { uiSettings } = useSelector(userSelector);

  const { updateColumns } = usePatchTableConfig({
    activeQueue,
  });

  const dispatch = useDispatch();

  const handleWidthChange: GridEventListener<'columnWidthChange'> = useCallback(
    changedColumn => {
      const udpateWidth = (col: AnnotationListColumn) => ({
        ...col,
        width:
          changedColumn.colDef.field === getColumnName(col)
            ? changedColumn.width
            : col.width,
      });

      // if table config has never been saved before, we construct a new one and save it here.
      const updatedColumns =
        tableConfigColumns.length > 0
          ? tableConfigColumns.map(udpateWidth)
          : getAnnotationListColumns(columns).map(udpateWidth);

      if (isUserAdminLike) updateColumns(updatedColumns);

      const widthToUpdate = {
        [changedColumn.colDef.field]: changedColumn.width,
      };
      const payload: UserTableConfig = activeQueue
        ? {
            ...uiSettings.documentsDashboard,
            queues: {
              width: { [activeQueue.id]: widthToUpdate },
            },
          }
        : {
            ...uiSettings.documentsDashboard,
            organization: { width: widthToUpdate },
          };

      dispatch(
        updateUiSettings({
          documentsDashboard: payload,
        })
      );
    },
    [
      uiSettings,
      dispatch,
      isUserAdminLike,
      updateColumns,
      columns,
      tableConfigColumns,
      activeQueue,
    ]
  );

  const handleColumnOrderChange: GridEventListener<'columnOrderChange'> =
    useCallback(
      changedColumn => {
        // if table config has never been saved before, we construct a new one and save it here.
        if (tableConfigColumns.length === 0) {
          const targetColumns = getAnnotationListColumns(columns);

          const reorderedColumns = swapItemsInArray(
            targetColumns,
            // - 1 is here because of the first column being the checkbox
            changedColumn.oldIndex - 1,
            changedColumn.targetIndex - 1
          );

          return updateColumns(reorderedColumns);
        }

        const reorderedColumns = swapItemsInArray(
          columns,
          // - 1 is here because of the first column being the checkbox
          changedColumn.oldIndex - 1,
          changedColumn.targetIndex - 1
        );

        const updatedColumns = preserveMissingColumns({
          previousColumns: tableConfigColumns,
          updatedColumns: getAnnotationListColumns(reorderedColumns),
        });

        return updateColumns(updatedColumns);
      },
      [updateColumns, columns, tableConfigColumns]
    );

  return {
    pinnedColumns,
    handleWidthChange,
    handleColumnOrderChange,
    isDisabled: !isUserAdminLike,
  };
};
